import {
  attachValidation,
  EditableAccessRule,
  PersistedAccessRule,
  ValidationError,
} from "../../models";
import {
  areDatesEqual,
  areTimesEqual,
  getEndOfDay,
  getStartOfDay,
  normalizeToDateOnly,
  normalizeToTimeOfDaySeconds,
  parseTimeOrEndOfDay,
  parseTimeOrStartOfDay,
  toValidDate,
} from "../ruleEditorTimeUtils";

export interface BlackoutDateRangeIdentity {
  blackoutDateStart?: Date | string | null;
  blackoutDateEnd?: Date | string | null;
  blackoutTimeSlotStart?: Date | string | null;
  blackoutTimeSlotEnd?: Date | string | null;
}

export interface BlackoutDateRangeAccessRule extends PersistedAccessRule {
  blackoutDateStart: string;
  blackoutDateEnd: string;
  blackoutTimeSlotStart?: string;
  blackoutTimeSlotEnd?: string;
}

export interface EditableBlackoutDateRangeAccessRule extends EditableAccessRule {
  blackoutDateStart: Date | null;
  blackoutDateEnd: Date | null;
  blackoutTimeSlotStart: Date | null;
  blackoutTimeSlotEnd: Date | null;
}

export const blackoutDateRangeDiffAdapter = {
  isDeleted: (rule: EditableBlackoutDateRangeAccessRule) =>
    rule.isNew === false &&
    rule.blackoutDateStart == null &&
    rule.blackoutDateEnd == null &&
    rule.blackoutTimeSlotStart == null &&
    rule.blackoutTimeSlotEnd == null,

  isUpdated: (
    original: BlackoutDateRangeAccessRule,
    current: EditableBlackoutDateRangeAccessRule,
  ) =>
    !areDatesEqual(original.blackoutDateStart, current.blackoutDateStart) ||
    !areDatesEqual(original.blackoutDateEnd, current.blackoutDateEnd) ||
    !areTimesEqual(
      original.blackoutTimeSlotStart,
      current.blackoutTimeSlotStart,
    ) ||
    !areTimesEqual(original.blackoutTimeSlotEnd, current.blackoutTimeSlotEnd),

  getIdentityKey: getBlackoutDateRangeIdentityKey,
};

export const toEditableBlackoutDateRange = (
  rule: BlackoutDateRangeAccessRule,
): EditableBlackoutDateRangeAccessRule =>
  attachValidation(
    {
      ...rule,
      localRuleId: crypto.randomUUID(),
      isNew: false,
      blackoutDateStart: toValidDate(rule.blackoutDateStart),
      blackoutDateEnd: toValidDate(rule.blackoutDateEnd),
      blackoutTimeSlotStart: parseTimeOrStartOfDay(rule?.blackoutTimeSlotStart),
      blackoutTimeSlotEnd: parseTimeOrEndOfDay(rule?.blackoutTimeSlotEnd),
    },
    validate,
  );

export const createEmptyEditableBlackoutDateRangeRule = (
  overrides?: Partial<EditableBlackoutDateRangeAccessRule>,
): EditableBlackoutDateRangeAccessRule =>
  attachValidation(
    {
      localRuleId: crypto.randomUUID(),
      isNew: true,
      liftAccessRuleId: null,
      blackoutDateStart: null,
      blackoutDateEnd: null,
      blackoutTimeSlotStart: getStartOfDay(),
      blackoutTimeSlotEnd: getEndOfDay(),
      ...overrides,
    },
    validate,
  );

function validate(
  rule: EditableBlackoutDateRangeAccessRule,
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (
    rule.blackoutDateStart == null ||
    rule.blackoutDateEnd == null ||
    rule.blackoutTimeSlotStart == null ||
    rule.blackoutTimeSlotEnd == null
  ) {
    errors.push({
      messageKey: "ACCESS_RULE_VALIDATION_BLACKOUT_DATE_REQUIRED_FIELD_MISSING",
    });
  } else if (
    rule.blackoutDateStart != null &&
    rule.blackoutDateEnd != null &&
    rule.blackoutDateStart > rule.blackoutDateEnd
  ) {
    errors.push({
      messageKey:
        "ACCESS_RULE_VALIDATION_BLACKOUT_DATE_RANGE_DATE_START_BEFORE_END",
    });
  } else if (
    rule.blackoutTimeSlotStart != null &&
    rule.blackoutTimeSlotEnd != null &&
    rule.blackoutTimeSlotStart > rule.blackoutTimeSlotEnd
  ) {
    errors.push({
      messageKey:
        "ACCESS_RULE_VALIDATION_BLACKOUT_DATE_RANGE_TIME_START_BEFORE_END",
    });
  }

  return errors;
}

function ruleHasValidDates(rule: EditableBlackoutDateRangeAccessRule) {
  return (
    rule.blackoutDateStart instanceof Date &&
    !isNaN(rule.blackoutDateStart.getTime()) &&
    rule.blackoutDateEnd instanceof Date &&
    !isNaN(rule.blackoutDateEnd.getTime())
  );
}

export function hasAnyDateRangeOverlap(
  rules: EditableBlackoutDateRangeAccessRule[],
): boolean {
  const validRules = rules
    .filter(ruleHasValidDates)
    .sort(
      (a, b) => a.blackoutDateStart!.getTime() - b.blackoutDateStart!.getTime(),
    );

  for (let i = 1; i < validRules.length; i++) {
    const prev = validRules[i - 1];
    const curr = validRules[i];

    if (prev.blackoutDateEnd! >= curr.blackoutDateStart!) {
      return true;
    }
  }

  return false;
}

function blackoutDateRangesOverlap(
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date,
): boolean {
  return aStart <= bEnd && aEnd >= bStart;
}

export function findOverlappingBlackoutDateRangeRuleIds(
  rules: EditableBlackoutDateRangeAccessRule[],
): Set<string> {
  const erroredRuleIds = new Set<string>();

  const validRules = rules.filter((r) => ruleHasValidDates(r));

  for (let i = 0; i < validRules.length; i++) {
    for (let j = i + 1; j < validRules.length; j++) {
      const a = validRules[i];
      const b = validRules[j];

      if (
        blackoutDateRangesOverlap(
          a.blackoutDateStart!,
          a.blackoutDateEnd!,
          b.blackoutDateStart!,
          b.blackoutDateEnd!,
        )
      ) {
        erroredRuleIds.add(a.localRuleId);
        erroredRuleIds.add(b.localRuleId);
      }
    }
  }

  return erroredRuleIds;
}

/**
 * Identity key for blackout date-range rules
 * de-duplication
 * - Ignores IDs and lifecycle flags
 * - Dates are normalized to YYYY-MM-DD
 * - Time slots are normalized to HH:mm:ss
 */
function getBlackoutDateRangeIdentityKey(
  rule: BlackoutDateRangeIdentity,
): string {
  return [
    normalizeToDateOnly(rule.blackoutDateStart),
    normalizeToDateOnly(rule.blackoutDateEnd),
    normalizeToTimeOfDaySeconds(rule.blackoutTimeSlotStart),
    normalizeToTimeOfDaySeconds(rule.blackoutTimeSlotEnd),
  ].join("|");
}
